import React from 'react'

const Link = React.forwardRef(
  (
    {
      children,
      href,
      target,
      rel,
      underline = 'hover',
      color = 'primary',
      variant = 'inherit',
      sx = {},
      className = '',
      onClick,
      ...props
    },
    ref
  ) => {
    // Get link styles based on underline and color
    const getLinkStyles = () => {
      const baseStyles = {
        color: 'inherit',
        textDecoration: 'none',
        backgroundColor: 'transparent',
        outline: 0,
        cursor: 'pointer',
        transition:
          'color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
        '&:hover': {
          textDecoration: 'underline'
        },
        '&:focus': {
          outline: '2px solid #1976d2',
          outlineOffset: '2px'
        }
      }

      // Color variations
      const colorStyles = {
        inherit: {
          color: 'inherit'
        },
        primary: {
          color: '#1976d2',
          '&:hover': {
            color: '#1565c0',
            textDecoration: 'underline'
          }
        },
        secondary: {
          color: '#9c27b0',
          '&:hover': {
            color: '#7b1fa2',
            textDecoration: 'underline'
          }
        },
        error: {
          color: '#d32f2f',
          '&:hover': {
            color: '#c62828',
            textDecoration: 'underline'
          }
        },
        warning: {
          color: '#ed6c02',
          '&:hover': {
            color: '#e65100',
            textDecoration: 'underline'
          }
        },
        info: {
          color: '#0288d1',
          '&:hover': {
            color: '#01579b',
            textDecoration: 'underline'
          }
        },
        success: {
          color: '#2e7d32',
          '&:hover': {
            color: '#1b5e20',
            textDecoration: 'underline'
          }
        }
      }

      // Underline variations
      const underlineStyles = {
        none: {
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'none'
          }
        },
        hover: {
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'underline'
          }
        },
        always: {
          textDecoration: 'underline',
          '&:hover': {
            textDecoration: 'underline'
          }
        }
      }

      // Variant styles
      const variantStyles = {
        inherit: {
          fontFamily: 'inherit',
          fontSize: 'inherit',
          fontWeight: 'inherit',
          lineHeight: 'inherit'
        },
        body1: {
          fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
          fontSize: '1rem',
          fontWeight: 400,
          lineHeight: 1.5,
          letterSpacing: '0.00938em'
        },
        body2: {
          fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
          fontSize: '0.875rem',
          fontWeight: 400,
          lineHeight: 1.43,
          letterSpacing: '0.01071em'
        },
        button: {
          fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
          fontSize: '0.875rem',
          fontWeight: 500,
          lineHeight: 1.75,
          letterSpacing: '0.02857em',
          textTransform: 'uppercase'
        },
        caption: {
          fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
          fontSize: '0.75rem',
          fontWeight: 400,
          lineHeight: 1.66,
          letterSpacing: '0.03333em'
        },
        overline: {
          fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
          fontSize: '0.75rem',
          fontWeight: 400,
          lineHeight: 2.66,
          letterSpacing: '0.08333em',
          textTransform: 'uppercase'
        }
      }

      return {
        ...baseStyles,
        ...colorStyles[color],
        ...underlineStyles[underline],
        ...variantStyles[variant],
        ...sx
      }
    }

    const styles = getLinkStyles()

    // If href is provided, render as anchor tag
    if (href) {
      return (
        <a
          ref={ref}
          href={href}
          target={target}
          rel={rel}
          className={className}
          style={styles}
          onClick={onClick}
          {...props}
        >
          {children}
        </a>
      )
    }

    // Otherwise render as button
    return (
      <button
        ref={ref}
        className={className}
        style={styles}
        onClick={onClick}
        type='button'
        {...props}
      >
        {children}
      </button>
    )
  }
)

Link.displayName = 'Link'

export default Link
